import { clamp } from '@/core/rng';

/**
 * What an act costs a god — Design III §86–§90, the divine economy.
 *
 * Until now a cast was free. Only the Anchor spent anything, so §87's
 * "precision and scale become strategic choices" had nothing to choose
 * between: a whisper over one tree and a storm across a watershed were the
 * same price, which is no price. Silence (§179) could not be *strategically*
 * valid, because there was nothing to preserve by keeping it.
 *
 * ## The resource already existed under another name
 *
 * `GodState.power` **is** Design III's Impulse: 0..1, spent by the Anchor,
 * regenerating at `POWER_PER_DAY`. It is not renamed here because the name is
 * in a snapshot and the rename is churn, but the mapping is worth stating once
 * so nobody builds a *second* pool beside it:
 *
 * | Design III | here |
 * |---|---|
 * | `currentImpulse` | `GodState.power` |
 * | `maxImpulse` | 1, the clamp |
 * | `regenRate` | `POWER_PER_DAY`, 0.02 — fifty days from empty |
 *
 * `GodState.impulse` is **not** this. It is an NPC temperament — how readily a
 * god acts at all — and the collision of names is exactly why this note is
 * here.
 *
 * ## The shape
 *
 *     cost = BASE × focus × reach × environment
 *
 * §86 also lists contest and duration; neither has a system yet, so neither is
 * invented here. What is present is what can be honestly computed today.
 *
 * ## Why it is not a price list
 *
 * §89: *gods are powerful because they understand leverage.* The environment
 * term is the whole point of the design — Rain into air that was going to rain
 * anyway is cheap, Rain into a dry clear sky is dear — because it makes
 * §90 true: `DISCOVERY → UNDERSTANDING → LEVERAGE → EFFICIENT INTERVENTION`.
 * A player who knows the land is *mechanically* stronger than one who does
 * not, with no unlock involved.
 */

/**
 * The reference cast, in metres. Must equal `FOCUS_REFERENCE.radius` in
 * `simulation.ts`; asserted in `test/leverage.ts` rather than imported,
 * because simulation.ts imports *this* and a cycle helps nobody.
 */
export const REFERENCE_RADIUS = 420;

/** What a reference-sized cast of each verb costs, as a share of the whole. */
export const VERB_BASE: Record<string, number> = {
  // the three that write into the land, in rough order of how much they move
  rain: 0.0080,
  strengthen: 0.0095,
  weaken: 0.0085,
  // a strike is one ignition, not an area effect — cheap to start, and the
  // fire that follows is the world's, not the god's
  strike: 0.0060,
  // Omen writes nothing into the land at all (§11): it costs a fraction, and
  // it is the verb a poor god can still afford, which is the point of it
  omen: 0.0030,
};

/**
 * How cost grows with the size of the cast.
 *
 * Not with area. Area would make a watershed cast 640,000× a tree, which is
 * not a choice, it is a wall. The honest exponent comes from the model
 * already in `simulation.ts`: concentration falls as `(ref/r)^1.1`, so the
 * total work a cast does is `area × concentration ∝ r^0.9`. **Cost tracks the
 * work**, so a cast is priced by what it actually moves.
 */
export const FOCUS_EXP = 0.9;

export const focusFactor = (radius: number): number =>
  Math.pow(Math.max(1, radius) / REFERENCE_RADIUS, FOCUS_EXP);

/**
 * §88, reach. Acting where you are is the cheapest thing a god can do; acting
 * across the island is not. This is what makes travel matter and what makes an
 * Anchor worth more than the resonance it gathers — standing in the world has
 * a mechanical price, and a stone you left behind pays part of it.
 *
 * `anchorNear` is the distance to the nearest anchor of this god's, if any.
 * Inside an anchor's reach the cast is priced as if the god were standing
 * there, which is precisely what "remember me here" should buy.
 */
export const REACH_SCALE = 2600;   // metres at which reach has doubled the cost
export const REACH_EXP = 1.15;

export function reachFactor(distance: number, anchorNear = Infinity): number {
  const d = Math.max(0, Math.min(distance, anchorNear));
  return 1 + Math.pow(d / REACH_SCALE, REACH_EXP);
}

/** What the land is like where the cast lands, as far as cost is concerned. */
export interface Conditions {
  /** 0..1 climate wetness at the target. */
  rain: number;
  /** Biomass deviation already there, signed — what the land remembers. */
  biomass: number;
  /** Moisture deviation already there, signed. */
  moisture: number;
  /**
   * The target's own drought-holding, when the cast is channelled into a
   * plant whose strain is known (2026-08-19, the wake) — `strain.ts`'s
   * tolerance axis reaching "processViability's wet", exactly as its contract
   * says. A hardy variety's growth is a viable process further into the dry
   * than ordinary ground suggests, so a god who knows its strains works with
   * them (law 17). Absent means 1, which is every caller that predates it.
   */
  tolerance?: number;
}

/**
 * §89, environmental leverage — the term that rewards knowing the world.
 *
 * Each verb is cheap where the land was already going that way and dear where
 * it was not. The range is deliberately wide (about 0.55× to 2.2×), because a
 * factor that barely moves is a factor nobody learns.
 */
export const LEV_MIN = 0.55, LEV_MAX = 2.2;

export function environmentFactor(verb: string, c: Conditions): number {
  const wet = clamp((c.rain + c.moisture * 0.5) * (c.tolerance ?? 1), 0, 1);
  let f: number;
  switch (verb) {
    // rain into air that was going to rain anyway
    case 'rain': f = 1.9 - 1.3 * wet; break;
    // growth is cheap on ground that can carry it: wet, and not already stripped
    case 'strengthen': f = 1.95 - 1.15 * wet - 0.25 * clamp(c.biomass, 0, 1); break;
    // killing what is already struggling is easy; killing a thriving wet stand
    // is not, which is why a drought is a god's opportunity
    case 'weaken': f = 0.75 + 1.15 * wet + 0.2 * clamp(c.biomass, 0, 1); break;
    // fire wants dry fuel. Wet ground resists an ignition and always has —
    // `fire.ts` may refuse it outright, and this makes trying expensive first
    case 'strike': f = 0.7 + 1.5 * wet; break;
    // an omen is not a physical act, so the land has no say in it
    default: f = 1;
  }
  return clamp(f, LEV_MIN, LEV_MAX);
}

/**
 * How much of the process a verb reinforces is *already happening here*.
 *
 * Design IV §14: Strengthen reinforces **an existing viable process** — on a
 * plant, growth; on a fire, combustion; on a migration, migratory pressure.
 * That word "existing" is the whole design, and the build did not honour it:
 * `bloom` wrote the same `+0.6` biomass onto a rainforest and onto bare desert
 * rock, which is object-shaped thinking (III §45) wearing a process-shaped
 * coat. **You cannot strengthen a process that is not happening.**
 *
 * So growth verbs scale by how viable growth actually is at the spot: the
 * climate's own wetness, what the biome can carry, and what the land already
 * remembers. Weaken scales the same way and for the same reason — there is
 * nothing to diminish on ground that grows nothing.
 *
 * **There is a floor.** Acting against the land must be *difficult* (§89), not
 * *pointless*: a quarter effect at roughly double cost is about eight times
 * worse per unit of god, which is a lesson. Zero effect would not be a lesson,
 * it would be a wall, and walls teach nothing.
 */
export const VIABILITY_FLOOR = 0.25;

/**
 * What the Godhood does to viability — three variables, and they are three
 * because the Web sells three different beliefs about working with the land.
 *
 * `floor` is `process.viabilityFloor`, an ADD: it raises the least a cast can
 * ever accomplish on ground that has nothing to work with. It is the only one
 * of the three that can legitimately be negative (an Epic sells −0.15), which
 * is why it may never travel through `Simulation.mod` — that channel's guard
 * rewrites any non-positive to 1. It is passed in.
 *
 * `high` and `low` are multipliers on the two ends of the same curve, and they
 * are applied to the DEVIATION from the floor rather than to the result, so
 * that raising one cannot quietly move the other's end of the scale.
 */
export interface ViabilityMods { floor?: number; high?: number; low?: number }

export function processViability(
  verb: string, c: Conditions, carrying = 0.5, mods: ViabilityMods = {},
): number {
  // Omen touches no process in the land, and Strike introduces its own energy
  // rather than reinforcing anything — neither is scaled.
  if (verb === 'omen' || verb === 'strike') return 1;
  // Rain *introduces* moisture rather than reinforcing a process, which is
  // precisely why Design IV §19 makes it a Signature Expression and not one of
  // the four. It is not scaled either.
  if (verb === 'rain') return 1;
  const wet = clamp((c.rain + c.moisture * 0.5) * (c.tolerance ?? 1), 0, 1);
  // growth needs water and somewhere to put it; a stand already stripped to
  // nothing has little left to reinforce, and little left to take
  const growing = clamp(0.15 + 0.85 * wet, 0, 1)
                * clamp(0.35 + 0.65 * carrying, 0, 1)
                * clamp(1 + c.biomass * 0.5, 0.4, 1.4);
  const g = clamp(growing, 0, 1);
  // `low` is what a god gets out of ground that is barely doing anything, and
  // `high` is what it gets out of ground that is already thriving. They are
  // opposite ends of one curve, so they are blended by where on it this cast
  // landed rather than applied together — a god that has bought both does not
  // get both at once, which is law 26 refusing to let a build have no gaps.
  const floor = clamp(VIABILITY_FLOOR + (mods.floor ?? 0), 0, 1);
  const gain = (1 - floor) * g;
  const lift = (mods.low ?? 1) * (1 - g) + (mods.high ?? 1) * g;
  return clamp(floor + gain * lift, 0, 1);
}

/**
 * What it costs to physically pick something up — Design IV §21–§27.
 *
 * Grasp is a faculty of Presence, not an Expression: it is *touching the
 * world*, and the world continues from wherever the thing lands. So it is
 * priced quite differently from a cast. There is no focus term, because
 * IV §29 keeps Grasp individual and local on purpose — that distinction is
 * what separates touching the world from exerting influence across it — and
 * no environment term, because leverage is about processes and a rock is not
 * a process.
 *
 * What it *is* priced by is mass and attachment (IV §25), both steeply:
 * *pebble trivial, person manageable, tree substantial, boulder expensive,
 * mountain impossible.* The impossible end is not a special case — it falls
 * out of the exponent, exactly as an unaffordable watershed cast does.
 */
export const GRASP_BASE = 0.0012;
/** Mass, in kg, that costs `GRASP_BASE`. Everything is relative to this. */
export const GRASP_REFERENCE_MASS = 40;
/** Above 1 the price runs away, which is what makes a mountain impossible. */
export const GRASP_MASS_EXP = 1.25;

export function graspCost(massKg: number, attachment: number): number {
  const m = Math.pow(Math.max(0.05, massKg) / GRASP_REFERENCE_MASS, GRASP_MASS_EXP);
  // attachment is a multiplier, not an addend: tearing a thing free of the
  // ground is proportionally harder the heavier it already was
  const hold = 1 + 3.2 * clamp(attachment, 0, 1);
  return GRASP_BASE * m * hold;
}

/**
 * What being picked up does to the thing.
 *
 * IV §22: this is physical, not an editor — *roots resist, the trunk bends,
 * soil disturbs, roots eventually tear, the living tree becomes damaged.* So
 * lifting something attached hurts it, and lifting something loose does not.
 * A stone does not care; a rooted clump very much does.
 *
 * Returns 0..1 of damage. What that *means* belongs to whatever system owns
 * the thing — this only says how roughly it was handled.
 */
export const graspDamage = (attachment: number, living: boolean): number =>
  clamp(attachment * (living ? 1.15 : 0.35), 0, 1);

export interface CastCost {
  /** Share of the whole god, 0..1. */
  total: number;
  focus: number; reach: number; environment: number; base: number;
}

/** The whole calculation, kept in one place so the HUD and the sim agree. */
export function costOf(
  verb: string, radius: number, distance: number,
  conditions: Conditions, anchorNear = Infinity,
  /**
   * What the acting god's Godhood has done to the price.
   *
   * `all` scales the whole bill (`act.cost`); `reach` scales the distance term
   * alone (`impulse.cost.distance`), so a Reach god gets cheap at range without
   * everything becoming cheap. Both default to 1, which is what an unspent Web
   * returns, so every existing caller is unaffected.
   *
   * They are passed rather than looked up because this function is the ONE
   * place the price lives — `hud.ts`'s preview and `simulation.act` both call
   * it, and a modifier applied in only one of them is a quote that lies.
   * `main.ts`'s `castMods` and `simulation.act`'s own call site are that pair,
   * and anything added here has to be added to BOTH of them.
   */
  mods: {
    all?: number; reach?: number; focus?: number;
    /**
     * What the cast is AIMED AT, priced — `impulse.cost.beastTarget` (Beasts,
     * *Light Hand*) is the first: a multiplier on the whole bill when the
     * target is a herd or an animal, 1 for everything else. Passed by the
     * caller because `costOf` does not read a target kind and should not
     * (law 13); what it takes is a number, and the two call sites — the
     * quote and the bill — each resolve it from the thing they are pointing
     * at, so a discount the sim charges is a discount the bar shows.
     */
    target?: number;
  } = {},
): CastCost {
  const base = VERB_BASE[verb] ?? VERB_BASE.rain;
  /**
   * `focus` is what WIDTH costs, and `act.cost.tightFocus` is the Epic *One
   * Perfect Act* buying it down for a god that has committed to precision.
   *
   * **It is applied to the factor, not to its deviation from 1, and the first
   * version of this got that backwards.** `reach` one line down scales its
   * deviation because `reachFactor` is a PENALTY — always ≥ 1 — so shrinking
   * the deviation shrinks the penalty. `focusFactor` is not a penalty: it
   * crosses 1 at the reference radius, so below the reference it is already a
   * discount, and scaling *that* deviation toward 1 takes the discount away.
   * Measured, before the sign was fixed: a 134 m cast went from 0.004203 to
   * 0.008732 — the Epic for precision made precision cost twice as much.
   *
   * So it multiplies the term directly, and only where the term is about
   * precision at all. The split is `REFERENCE_RADIUS`, the same line
   * `Simulation.act` splits `act.work.tightFocus` from `act.work.wideFocus`
   * on, and it is the one radius the system already calls neutral. A wide cast
   * is deliberately untouched: law 26 wants genuine gaps, and a discount that
   * applied everywhere would be `act.cost` under a second name.
   */
  const focus = focusFactor(radius)
    * (radius <= REFERENCE_RADIUS ? (mods.focus ?? 1) : 1);
  const reach = 1 + (reachFactor(distance, anchorNear) - 1) * (mods.reach ?? 1);
  const environment = environmentFactor(verb, conditions);
  const total = base * focus * reach * environment * (mods.all ?? 1) * (mods.target ?? 1);
  return { total, focus, reach, environment, base };
}

/**
 * §113: the player is told a *category* and the reasons, never a number.
 *
 * Bands are set against what a day of regeneration buys (`POWER_PER_DAY`,
 * 0.02): "low" is something a god recovers from before tomorrow, "extreme" is
 * a commitment of weeks that had better be worth it.
 */
export type CostBand = 'low' | 'moderate' | 'high' | 'extreme';

export function costBand(total: number): CostBand {
  if (total < 0.02) return 'low';        // under a day of recovery
  if (total < 0.08) return 'moderate';   // under a week
  if (total < 0.25) return 'high';       // under a fortnight
  return 'extreme';
}

/**
 * Why it costs what it costs, in the world's voice — the reasons §113 asks
 * for. Ordered by how much each term actually moved the price, so the first
 * clause is always the one worth acting on.
 */
export function costReasons(c: CastCost): string[] {
  const out: Array<{ weight: number; say: string }> = [];
  if (c.focus > 1.6) out.push({ weight: c.focus, say: 'a wide attention' });
  else if (c.focus < 0.5) out.push({ weight: 1 / c.focus, say: 'a narrow attention' });
  if (c.reach > 1.5) out.push({ weight: c.reach, say: 'far from you' });
  if (c.environment > 1.35) out.push({ weight: c.environment, say: 'against the land' });
  else if (c.environment < 0.8) out.push({ weight: 1 / c.environment, say: 'with the land' });
  return out.sort((a, b) => b.weight - a.weight).map((o) => o.say);
}
